import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';
import { getIdToken } from './firebase';

/** Imágenes y descargas de qr-api que requieren sesión: un `<img src>` no manda el header Authorization (R14). */

export interface AuthBlobOptions {
  /** Organización activa (`X-Org-Id`); el operador no la necesita. */
  orgId?: string;
  signal?: AbortSignal;
}

function apiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
}

export async function fetchAuthBlob(path: string, options: AuthBlobOptions = {}): Promise<Blob> {
  const headers: Record<string, string> = { Accept: 'image/*' };
  const token = await getIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.orgId) headers['X-Org-Id'] = options.orgId;
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl()}${path}`, { headers, signal: options.signal });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') throw err;
    throw new ApiError({ kind: 'network', status: 0, message: 'No pudimos conectarnos. Revisa tu conexión.', details: err });
  }
  if (!response.ok) {
    const kind = response.status === 401 ? 'unauthorized' : response.status === 403 ? 'forbidden' : response.status === 404 ? 'not_found' : 'server';
    throw new ApiError({ kind, status: response.status, message: `Error ${response.status}` });
  }
  return response.blob();
}

export interface AuthImageState {
  url: string | null;
  loading: boolean;
  error: unknown;
  retry: () => void;
}

/** Descarga la imagen con sesión y la expone como object URL; lo revoca al cambiar de imagen o desmontar. */
export function useAuthImage(path: string | null, options: { orgId?: string; enabled?: boolean } = {}): AuthImageState {
  const { orgId, enabled = true } = options;
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path || !enabled) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    fetchAuthBlob(path, { orgId, signal: controller.signal })
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [path, orgId, enabled, tick]);

  const retry = useCallback(() => setTick((t) => t + 1), []);
  return { url, loading: loading || (enabled && Boolean(path) && !url && !error), error, retry };
}

/** Descarga un archivo con sesión (`download=1`) mediante un enlace temporal. */
export async function downloadAuthFile(path: string, filename: string, options: AuthBlobOptions = {}): Promise<void> {
  const blob = await fetchAuthBlob(path, options);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
